import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo } from 'react'
import { getRevenueOverview } from '../../../services'
import type {
    ProductGroupMonthlyRevenue,
    RevenueOverviewParams,
} from '../../../types'

export interface ColumnTotals {
    monthly: Record<
        string,
        {
            allocated_previous_year: number
            allocated_current_year: number
            unallocated: number
        }
    >
    allocatedRenewal: number
    allocatedNewSale: number
    allocatedUpsell: number
    unallocatedRenewal: number
    unallocatedNewSale: number
    unallocatedUpsell: number
}

export function useOverviewData(filters: RevenueOverviewParams) {
    const { data: overviewData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS,
            'overview-pivot',
            filters,
        ],
        queryFn: () => getRevenueOverview(filters),
        enabled: !!filters.gte && !!filters.lte,
        staleTime: 5 * 60 * 1000,
    })

    const { productGroups, months, grandTotal } = useMemo(() => {
        if (!overviewData) {
            return {
                productGroups: [] as ProductGroupMonthlyRevenue[],
                months: [] as string[],
                grandTotal: 0,
            }
        }
        return {
            productGroups: overviewData.product_groups,
            months: overviewData.months,
            grandTotal: overviewData.grand_total,
        }
    }, [overviewData])

    // Calculate totals for each column
    const columnTotals = useMemo<ColumnTotals>(() => {
        const totals: ColumnTotals['monthly'] = {}
        let allocatedRenewal = 0
        let allocatedNewSale = 0
        let allocatedUpsell = 0
        let unallocatedRenewal = 0
        let unallocatedNewSale = 0
        let unallocatedUpsell = 0

        months.forEach((month) => {
            totals[month] = {
                allocated_previous_year: 0,
                allocated_current_year: 0,
                unallocated: 0,
            }
        })

        productGroups.forEach((group) => {
            months.forEach((month) => {
                const monthData = group.monthly_data[month]
                if (monthData) {
                    totals[month].allocated_previous_year +=
                        monthData.allocated_previous_year
                    totals[month].allocated_current_year +=
                        monthData.allocated_current_year
                    totals[month].unallocated += monthData.unallocated
                }
            })
            allocatedRenewal += group.allocated_renewal
            allocatedNewSale += group.allocated_new_sale
            allocatedUpsell += group.allocated_upsell
            unallocatedRenewal += group.unallocated_renewal
            unallocatedNewSale += group.unallocated_new_sale
            unallocatedUpsell += group.unallocated_upsell
        })

        return {
            monthly: totals,
            allocatedRenewal,
            allocatedNewSale,
            allocatedUpsell,
            unallocatedRenewal,
            unallocatedNewSale,
            unallocatedUpsell,
        }
    }, [productGroups, months])

    return { productGroups, months, grandTotal, columnTotals, isLoading }
}
